import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { keepPreviousData, useMutation, useQuery } from '@tanstack/react-query';
import { Copy, ExternalLink, GitFork, Info } from 'lucide-react';
import { reuseOf, type ContentSource, type ReuseKind, type ReusePolicy } from '@ready/shared';
import { useApi, type ContentOwner } from '@/lib/api';
import { useDebounced } from '@/lib/use-debounced';
import { Badge, Button, Card, ErrorText } from '@/components/ui';
import { SearchBox } from '@/components/catalog';

/*
 * Reusing content (see @ready/shared reuse.ts): start new content from
 * something you may reuse, see where a copy came from, and choose what
 * others may do with yours.
 */

const NOUN: Record<ReuseKind, string> = { course: 'course', quiz: 'quiz', event: 'event' };
const PUBLIC_PATH: Record<ReuseKind, string> = { course: 'c', quiz: 'q', event: 'e' };
const EDITOR: Record<ReuseKind, (id: string) => string> = {
  course: (id) => `/courses/${id}/edit`,
  quiz: (id) => `/quizzes/${id}/edit`,
  event: (id) => `/events/${id}/edit`,
};

/**
 * "Or start from existing": search what this owner may reuse (Nirvik's
 * library, its own and its parents' content) and make a draft copy.
 */
export function StartFrom({ kind, owner }: { kind: ReuseKind; owner: ContentOwner }) {
  const api = useApi();
  const navigate = useNavigate();
  const [text, setText] = useState('');
  const q = useDebounced(text.trim());
  const items = useQuery({
    queryKey: ['reusable', kind, owner.kind === 'org' ? owner.orgId : 'library', q],
    queryFn: () => api.reusable(owner, kind, q),
    placeholderData: keepPreviousData,
  });
  const fork = useMutation({
    mutationFn: (id: string) => api.fork(kind, id, owner),
    onSuccess: (copy) => navigate(EDITOR[kind](copy.id)),
  });
  const customize = useMutation({
    mutationFn: (id: string) => api.customize(kind === 'course' ? 'course' : 'quiz', id, owner),
    onSuccess: (copy) => navigate(EDITOR[kind](copy.id)),
  });
  const list = items.data?.items ?? [];

  return (
    <Card title="Or start from an existing one">
      <p className="mb-3 text-sm text-muted-foreground">
        {kind === 'event'
          ? 'Duplicate a workshop or webinar from'
          : `Customize a ${NOUN[kind]} (yours keeps receiving its updates) or make your own copy, from`}{' '}
        {owner.kind === 'library'
          ? "Nirvik's library"
          : "Nirvik's library, your organization or the organizations above it"}
        , then change anything. {kind === 'event' ? 'You add new dates.' : 'It starts as a draft.'}
      </p>
      <SearchBox
        value={text}
        onChange={setText}
        placeholder={`Search ${NOUN[kind] === 'quiz' ? 'quizzes' : `${NOUN[kind]}s`} by title`}
        label="Search content to copy"
      />
      <ErrorText error={items.error ?? fork.error ?? customize.error} />
      {items.isSuccess && list.length === 0 && (
        <p className="mt-3 text-sm text-muted-foreground">
          {q ? 'Nothing matches that title.' : 'Nothing to copy yet.'}
        </p>
      )}
      {list.length > 0 && (
        <ul className="mt-3 divide-y divide-border">
          {list.map((i) => (
            <li
              key={i.id}
              className="flex flex-wrap items-center justify-between gap-3 py-3 text-sm"
            >
              <div className="min-w-0">
                <p className="font-medium">{i.title}</p>
                <p className="mt-0.5 flex flex-wrap items-center gap-2 text-muted-foreground">
                  {i.own ? 'Yours' : `From ${i.ownerName}`}
                  {i.latestVersion !== null && <Badge>Version {i.latestVersion}</Badge>}
                  {i.status === 'draft' && <Badge>Draft</Badge>}
                </p>
              </div>
              <div className="flex flex-wrap gap-2">
                {i.status === 'published' && i.audience !== 'course' && (
                  <a
                    href={`/${PUBLIC_PATH[kind]}/${i.slug}`}
                    target="_blank"
                    rel="noreferrer"
                    className="-my-2 inline-flex items-center gap-1 py-2 text-primary hover:underline"
                  >
                    Preview <ExternalLink className="size-3.5" aria-hidden />
                  </a>
                )}
                {i.canCustomize && (
                  <Button
                    variant="secondary"
                    disabled={customize.isPending}
                    title="Your version follows the source: its new versions flow in, with your changes kept"
                    onClick={() => customize.mutate(i.id)}
                  >
                    <GitFork className="size-4" aria-hidden /> Customize
                  </Button>
                )}
                <Button
                  variant="secondary"
                  disabled={!i.canFork || fork.isPending}
                  title={i.canFork ? undefined : "Its owner doesn't allow copies"}
                  onClick={() => fork.mutate(i.id)}
                >
                  <Copy className="size-4" aria-hidden />
                  {kind === 'event' ? 'Duplicate' : 'Make a copy'}
                </Button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

/** "Copied from …", and a pointer to a newer version of the source when there is one. */
export function SourceBanner({
  kind,
  source,
  customized = false,
}: {
  kind: ReuseKind;
  source: ContentSource | null;
  /** A customized version that follows its source, rather than a copy. */
  customized?: boolean;
}) {
  if (!source) return null;
  if (customized)
    return (
      <div className="mb-4 flex items-start gap-3 rounded-[var(--radius)] border border-border bg-muted/40 p-3 text-sm">
        <GitFork className="mt-0.5 size-4 shrink-0 text-muted-foreground" aria-hidden />
        <p>
          Your customized version of <span className="font-medium">{source.title}</span> by{' '}
          {source.ownerName}, built from its version {source.version ?? '–'}. When it publishes a
          new version, this one is updated automatically with your changes kept
          {kind === 'course' ? ' (new lessons slot in where the source puts them)' : ''}.
        </p>
      </div>
    );
  const newer =
    source.version !== null &&
    source.latestVersion !== null &&
    source.latestVersion > source.version;
  return (
    <div className="mb-4 flex items-start gap-3 rounded-[var(--radius)] border border-border bg-muted/40 p-3 text-sm">
      <GitFork className="mt-0.5 size-4 shrink-0 text-muted-foreground" aria-hidden />
      <div>
        <p>
          Copied from <span className="font-medium">{source.title}</span> by {source.ownerName}
          {source.version !== null ? ` (version ${source.version})` : ''}. Your copy is yours to
          change; it doesn&rsquo;t update by itself.
        </p>
        {newer && (
          <p className="mt-1 flex items-center gap-1.5 text-primary-strong">
            <Info className="size-4" aria-hidden />
            The source has a newer version ({source.latestVersion}).
            {source.slug && (
              <a
                href={`/${PUBLIC_PATH[kind]}/${source.slug}`}
                target="_blank"
                rel="noreferrer"
                className="underline underline-offset-2"
              >
                See what&rsquo;s new
              </a>
            )}
          </p>
        )}
      </div>
    </div>
  );
}

/** What others may do with this: use it as is (courses: assign it) and make copies. */
export function ReuseSettings({
  kind,
  owner,
  value,
  onChange,
}: {
  kind: ReuseKind;
  owner: ContentOwner;
  value: ReusePolicy | null | undefined;
  onChange: (next: ReusePolicy) => void;
}) {
  const policy = reuseOf(value);
  const who = owner.kind === 'library' ? 'Organizations' : 'Organizations under this one';
  const options: [keyof ReusePolicy, string][] = [
    ...(kind === 'course'
      ? ([['use', `${who} can assign it to their members as is`]] as [keyof ReusePolicy, string][])
      : []),
    [
      'fork',
      `${who} can make their own copy${kind === 'event' ? ' to run again' : ' and change it'}`,
    ],
  ];
  return (
    <fieldset className="space-y-1">
      <legend className="mb-1 text-sm font-medium">Reuse</legend>
      {options.map(([key, label]) => (
        <label key={key} className="flex items-center gap-2 py-1 text-sm">
          <input
            type="checkbox"
            className="size-5 accent-primary"
            checked={policy[key]}
            onChange={(e) => onChange({ ...policy, [key]: e.target.checked })}
          />
          {label}
        </label>
      ))}
      <p className="text-xs text-muted-foreground">
        Changes apply to future copies; existing copies stay as they are.
      </p>
    </fieldset>
  );
}
